import React from "react";
import ReactDOM from "react-dom";
import ContextMenu from "../lib/ContextMenu";
import Animation from "../lib/Animation";
import Language from "../lib/Language";
import { Reservation } from "../models/Reservation";
import ReservationStatus, { Status } from "../lib/ReservationStatus";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import Mousetrap from "@timeedit/mousetrap";
import { TimeEdit } from "../lib/TimeEdit";
import { MillenniumDate, MillenniumWeek } from "@timeedit/millennium-time";
import { ENTRY_CLASS, CR_ORDERING, EntryKind, TClusterKindValue } from "../lib/EntryConstants";
import { TimeConstants } from "../lib/TimeConstants";
import { Macros } from "../models/Macros";
import { DragListener } from "../lib/DragListener";
import ReservationTooltip from "./ReservationTooltip";
import PaddingTooltip from "./PaddingTooltip";
import EntryLength from "./EntryLength";
import Log from "../lib/Log";
import ExamConstants from "../lib/ExamConstants";
import ReservationConflictInfoTooltip, {
    CONFLICT_INFO_TOOLTIP_CLASSNAME,
    CONFLICT_TOOLTIP_LEFT_PADDING,
} from "./ReservationConflictInfoTooltip";
import { Entry as EntryModel } from "../models/Entry";
import { TFieldValue } from "../types/components/SplitObjectDialog";
import { TEntryCssClasses } from "../types/models/TEntry";
import { Kind } from "../models/TemplateKind";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { TTooltipMousePosition, TTooltipReservation } from "../types/components/Tooltip";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import { TAPIStatusClass } from "../types/api/APIClassTypes";

const { EXAM_MODE } = ExamConstants;

const MIN_WIDTH_FOR_INFO_ICON = 20;
const MIN_HEIGHT_FOR_INFO_ICON = 14;
const MIN_HEIGHT_FOR_TEXT_AND_INFO = 30;

const CLICK_TIMEOUT = 200;
const LEFT_PADDING = 260;

type TEntryProps = {
    flags: LDFlagSet;
    calendarId: string;
    isLayerActive: boolean;
    setLayerContentProvider: (fn: (jsx: React.ReactElement) => void) => void;
    data: EntryModel;
    isTooltipVisible: boolean;
    unrenderTooltip: (id: string) => void;
    readOnlyCalendar: boolean;
    enableEditMode: (entry: EntryModel, opts?: {}, cb?: () => void) => void;
    isGroupMode: boolean;
    selectionGroup: EntryModel[];
    onCopy: (entry: EntryModel, event: Event) => void;
    activeLayer: number;
    disableMenu: boolean;
    selectionContainsGroups: boolean;
    openStaticReservationListAdd: (reservationIds: number[], add: boolean) => void;
    clusterKind: TClusterKindValue;
    isLegalGroup: boolean;
    createGroup: (idsArg?: number[]) => void;
    createGroupFromEntry: (entry: EntryModel) => void;
    deleteGroup: (groupIds: number[] | number) => void;
    onEditExceptions: (entry: EntryModel) => void;
    editGroup: (entry: EntryModel, groups: number[], date: MillenniumDate) => void;
    addToGroup: (entry: EntryModel) => void;
    splitReservation: (
        entry: EntryModel,
        cb: (res: {
            adjustedSize: number;
            mandatoryFieldValues: TFieldValue[];
            objectId: number;
        }) => void
    ) => void;
    isInOverlapView: boolean;
    showOverlappingEntries: (entry: EntryModel) => void;
    showOverlappingEntriesInList: (entry: EntryModel) => void;
    emailReservation: (reservationIds: number[], includeSelection?: boolean) => void;
    getVisibleReservationsInGroups: (groupIds: number[]) => number[];
    moveToWaitingList: (reservationIds: number[], singleReservation: boolean) => void;
    onDelete: (
        reservationIds: number[],
        shouldSendEmail: boolean,
        cb?: (success: boolean) => void
    ) => void;
    onClick: (
        entry: EntryModel,
        addToSelection?: boolean,
        shiftKey?: boolean,
        isEntryLocked?: boolean
    ) => void;
    isEntryLocked: boolean;
    styles: React.CSSProperties[];
    onDragStart: (
        currentEntry: EntryModel,
        type: string,
        event: MouseEvent,
        isHighRes?: boolean
    ) => void;
    onInfoOpen: (
        entry: EntryModel | EntryModel[] | number[],
        showPanel: boolean,
        editMode: boolean
    ) => void;
    onObjectInfo: (object: any, createCopy?: boolean, viewOnly?: boolean, title?: string) => void;
    renderTooltip: (el: React.JSX.Element, id: string) => void;
    requestTooltip: (entryKey: string | null, show: boolean, force?: boolean) => void;
    isTooltipLocked: boolean;
    entryKey: string;
    onDynamicReservationIdsChanged: (newDynamicIds: number[]) => void;
    classes: TEntryCssClasses[];
    templateKind: Kind;
    layerSize: {
        width: number;
        height: number;
    };
    isConflictMode: boolean;
    parentCalendarElemRef: HTMLElement | null;
    isVertical: boolean;
    isSizeMode: boolean;
    showLength: boolean;
    fluffySize: number;
    fluffyHasSizeType: boolean;
    sizeEntries: React.ReactElement[];
    borderAndTextOnly: boolean;
    isWeekCluster: boolean;
    selectedReservationIds: number[];
    clusterDepth: number;
    isActiveInfoEntry: boolean;
    isLayerShown: boolean;
    isTextless: boolean;
    showLayer: () => void;
    moveMemberToScratchpad?: (
        memberId: number,
        parentId: number,
        label: string,
        callback: (success: boolean) => void
    ) => void;
    isVisibleInCalendar: (reservationIds: number[]) => boolean;
    getClusterValues: (periodCombination: any) => unknown[];
} & TAppContextProp;

const getEntryIdentifier = (
    calendarId: string,
    reservationIds: number[],
    headerObjects: number[],
    isPadding: boolean,
    index: number
) => {
    return `c:${calendarId}:${reservationIds.join(":")}:h:${headerObjects.join(":")}:p:${
        isPadding ? "t" : "f"
    }+${index}`;
};

type TEntryState = {
    waitingForDeletion: boolean;
    /** Null until updateStatusData has answered. */
    availableStatuses: Status[] | null;
    currentStatus: Status | null;
    canCancel: boolean;
    canEmail: boolean;
    canHaveExceptions: boolean | null;
    hasExamId: boolean | null;
    hasExamValue: boolean;
    examValueLoaded: boolean;
    /** The reservations behind this entry, loaded when a tooltip is wanted. */
    reservationInfo: TTooltipReservation[] | null;
    /** Set for a moment after a click, so a double click is not two clicks. */
    onClickRecentlyCalled?: boolean;
    mousePosition?: TTooltipMousePosition | null;
    isDropTarget?: boolean;
    showEditMode?: boolean;
};

class Entry extends React.Component<TEntryProps, TEntryState> {
    state: TEntryState = {
        waitingForDeletion: false,
        availableStatuses: null,
        currentStatus: null,
        canCancel: false,
        canEmail: false,
        canHaveExceptions: null,
        hasExamId: null,
        hasExamValue: false,
        examValueLoaded: false,
        reservationInfo: null,
    };
    _isMounted: boolean = false;
    _contextMenuRefs = new Set();
    _isClickable: boolean = false;

    componentDidMount() {
        this._isMounted = true;

        this.connectContextMenu();

        if (_.contains([EntryKind.COMPLETE, EntryKind.GROUP_COMPLETE], this.props.data.kind)) {
            if (this.props.isLayerActive) {
                this.bindKeyboardShortcuts();
            }
        }
        this.registerMacros();
    }

    componentDidUpdate(prevProps) {
        if (
            _.contains([EntryKind.COMPLETE, EntryKind.GROUP_COMPLETE], this.props.data.kind) &&
            !_.contains([EntryKind.COMPLETE, EntryKind.GROUP_COMPLETE], prevProps.data.kind)
        ) {
            if (this.props.isLayerActive) {
                this.bindKeyboardShortcuts();
            }
        }

        if (this.props.styles.length !== prevProps.styles.length) {
            this.connectContextMenu();
        }

        if (this._contextMenuRefs.size === 0) {
            this.connectContextMenu();
        }
        if (this.props.isTooltipVisible === true && !this.state.reservationInfo) {
            this.loadTooltip(true);
        } else if (this.props.isTooltipVisible === false && prevProps.isTooltipVisible === true) {
            this.unrenderTooltip();
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ reservationInfo: null });
        }
    }

    connectContextMenu = () => {
        if (
            this.props.data.kind === EntryKind.NONE ||
            this.props.data.isPadding ||
            !this.props.data.readable ||
            this.props.disableMenu ||
            this.isWrongKind()
        ) {
            return;
        }

        this.props.styles.forEach((style, index) => {
            const key = getEntryIdentifier(
                this.props.calendarId,
                this.props.data.reservationids,
                this.props.data.headerObjects,
                this.props.data.isPadding,
                index
            );
            const element = document.getElementById(key);
            if (element && !this.props.data.isPadding) {
                this._contextMenuRefs.add(
                    ContextMenu.addListener(element, this.getMenuItems, ContextMenu.RIGHT_CLICK)
                );
            }
        });
    };

    disconnectContextMenu = () => {
        this._contextMenuRefs.forEach((ref) => {
            ContextMenu.removeListener(ref);
        });
    };

    registerMacros = () => {
        if (this.props.appContext.examIdField && this.props.data.reservationids.length === 1) {
            this.props.appContext.registerMacro(`entry${this.props.data.reservationids[0]}`, {
                events: [Macros.Event.RESERVATION_MADE_OR_MODIFIED],
                actions: [
                    {
                        key: Macros.Action.REFRESH,
                        action: (reservationIds: number[] = []) => {
                            if (reservationIds.indexOf(this.props.data.reservationids[0]) !== -1) {
                                this.setState({ examValueLoaded: false });
                            }
                        },
                    },
                ],
            });
        }
    };

    unrenderTooltip = () => {
        this.props.unrenderTooltip(
            this.props.data.isPadding
                ? `${this.props.data.reservationids.join(",")}:padding`
                : this.props.data.reservationids.join(",")
        );
    };

    bindKeyboardShortcuts = () => {
        if (this.props.data.isPadding) {
            return;
        }
        Mousetrap.unbindWithHelp("mod+i");
        Mousetrap.unbindWithHelp("mod+e");
        Mousetrap.unbindWithHelp("mod+c");
        Mousetrap.unbindWithHelp("mod+alt+j");
        Mousetrap.bindWithHelp(
            "mod+i",
            this.handleInfoShortcut.bind(this, false),
            undefined,
            Language.get("nc_cal_show_or_hide_reservation_info.")
        );

        Mousetrap.bindWithHelp(
            "mod+c",
            this.onCopy,
            undefined,
            Language.get("nc_copy_selected_reservation.")
        );

        const readOnly =
            this.props.readOnlyCalendar ||
            (this.props.data.lock && this.props.data.lock === "soft");
        const hasGroup = this.props.data.groups.length > 0;

        const editable = !(
            readOnly ||
            (!this.props.enableEditMode && !this.props.data.modifiable) ||
            (hasGroup && !this.props.isGroupMode) ||
            this.props.selectionGroup.length !== 0
        );
        if (editable) {
            Mousetrap.bindWithHelp(
                "mod+e",
                this.handleInfoShortcut.bind(this, true),
                undefined,
                Language.get("nc_cal_show_or_hide_reservation_info.")
            );
            Mousetrap.bindWithHelp(
                "mod+alt+j",
                () => {
                    this.props.enableEditMode(this.props.data);
                },
                undefined,
                Language.get("cal_func_res_change_object")
            );
        }
    };

    onCopy = (event) => {
        if (!window.getSelection()?.toString()) {
            this.props.onCopy(this.props.data, event);
        }
        return true;
    };

    componentWillUnmount() {
        this._isMounted = false;
        this.unrenderTooltip();
        this.disconnectContextMenu();
        if (_.contains([EntryKind.COMPLETE, EntryKind.GROUP_COMPLETE], this.props.data.kind)) {
            Mousetrap.unbindWithHelp("mod+i");
            Mousetrap.unbindWithHelp("mod+e");
            Mousetrap.unbindWithHelp("mod+c");
            Mousetrap.unbindWithHelp("mod+alt+j");
        }
        if (
            this.props.data &&
            this.props.data.reservationids &&
            this.props.data.reservationids.length === 1
        ) {
            this.props.appContext.deregisterMacro(`entry${this.props.data.reservationids[0]}`);
        }
    }

    setStatus = (status) => {
        if (this.state.currentStatus === status) {
            return;
        }

        let toChange = this.props.data.reservationids; // Always work with the current entry, even if it is part of a group
        if (this.useGroup()) {
            // But if multiple reservations have been clicked, work with the whole group
            toChange = this.getGroupIds();
        }

        const setStatus = () =>
            API.setReservationStatus(toChange, status.id, (result) => {
                const numSuccessful = result[0].filter((bool) => bool === true).length;
                const errors: TAPIStatusClass[] = result[1].filter((status) => status.details); // Status objects, reference is the reservation ID
                let errorDetails = "";
                if (errors.length > 0) {
                    errorDetails = errors.map((error) => error.details).join(", ");
                }
                if (numSuccessful === 0) {
                    Log.warning(
                        `${Language.get("nc_edit_reservation_status_change_error")} ${errorDetails}`
                    );
                    return;
                }

                if (numSuccessful < toChange.length) {
                    Log.warning(
                        `${Language.get(
                            "nc_edit_reservation_status_change_x_of_y_complete",
                            numSuccessful,
                            toChange.length
                        )} ${errorDetails}`
                    );
                } else {
                    Log.info(Language.get("nc_edit_reservation_status_change_complete"));
                }
                this.onUpdate();
                this.updateStatusData();
            });

        if (
            this.props.data.reservationids.length === 1 ||
            this.state.currentStatus !== ReservationStatus.UNKNOWN
        ) {
            setStatus();
            return;
        }

        const buttons = [
            { title: Language.get("dialog_yes"), cb: setStatus },
            { title: Language.get("dialog_no") },
        ];
        this.props.appContext.presentModal(
            <p>{Language.get("nc_confirm_entry_set_cluster_status")}</p>,
            null,
            null,
            buttons
        );
    };

    updateStatusData = (callback = _.noop) => {
        API.getReservationStatus(this.props.data.reservationids, (result) => {
            API.okToCancelReservations(
                { reservationIds: this.props.data.reservationids },
                (cancelResult) => {
                    this.setState({
                        currentStatus: ReservationStatus.statusForId(result[0].status),
                        availableStatuses: result[1].map((item) =>
                            ReservationStatus.statusForId(item.status)
                        ),
                        canCancel: _.every(cancelResult.parameters[0], (status) => !status.details),
                        canEmail: _.every(
                            cancelResult.parameters[1],
                            (canEmail) => canEmail === true
                        ),
                    });
                    callback();
                }
            );
        });
    };

    checkCanHaveExceptions = (callback = _.noop) => {
        API.canHaveMemberExceptionObjectsForReservations(
            this.props.data.reservationids,
            (result) => {
                this.setState({ canHaveExceptions: result }, callback);
            }
        );
    };

    isCapacityOptionAvailable = () => {
        // Check centralized flag
        // Then also, I guess, check options on the reservation
        // saying if it can have capacity or not
        return process.env.NODE_ENV === "development" || this.props.flags?.examFlowV3;
    };

    isSplitOptionAvailable = () => {
        return process.env.NODE_ENV === "development" || this.props.flags?.coreAmSplitFunctionality;
    };

    isSplitOptionDisabled = () => {
        return !(
            this.props.data.hasSize(this.props.appContext.user?.useSecondarySize ?? false) &&
            this.props.data.reservationids.length === 1
        );
    };

    updateMenus = () => {
        this._contextMenuRefs.forEach((ref) => ContextMenu.update(ref));
    };

    getMenuItems = () => {
        // Looks like we cant trush that "this" is pointing on this class even if we use arrow functions. So just be on the safe declare self and us it exclusively in this method.
        const self = this;

        const notActiveLayer =
            self.props.activeLayer !== 0 && self.props.data.layer !== self.props.activeLayer;
        if (
            !self.props.data.readable ||
            notActiveLayer ||
            self.props.disableMenu ||
            self.isWrongKind() ||
            self.props.data.isPadding
        ) {
            return [];
        }
        const readOnly =
            self.props.readOnlyCalendar ||
            (self.props.data.lock && self.props.data.lock === "soft");
        const useGroup = self.useGroup() && self.props.selectionGroup.length > 1;

        if (!self.state.availableStatuses) {
            self.updateStatusData(() => self.updateMenus());
        }
        if (self.state.canHaveExceptions === null) {
            self.checkCanHaveExceptions(() => self.updateMenus());
        }

        if (
            this.props.appContext.examIdField &&
            self.props.data.reservationids.length === 1 &&
            self.state.examValueLoaded === false
        ) {
            API.exportReservations(self.props.data.reservationids, false, (result) => {
                let examId: any = null;
                if (result.length > 0) {
                    examId = _.find(
                        result[0].fields ?? [],
                        (field) => field.id === this.props.appContext.examIdField
                    );
                }
                self.setState(
                    {
                        examValueLoaded: true,
                        hasExamId: examId !== null && examId !== undefined ? true : false,
                        hasExamValue:
                            examId &&
                            examId.values &&
                            examId.values.length > 0 &&
                            examId.values[0] !== "",
                    },
                    () => self.updateMenus()
                );
            });
        }

        const hasGroup = self.props.data.groups.length > 0;
        const isDeletable =
            !(hasGroup && !self.props.data.grouped) &&
            !(self.props.selectionGroup.length > 1 && self.props.selectionContainsGroups) &&
            !readOnly &&
            self.state.canCancel;

        const cancelSubmenu = [
            {
                key: "entry.cancel",
                label: hasGroup
                    ? Language.get("nc_cancel_reservation_group")
                    : Language.get("cal_func_res_delete"),
                isDisabled: !isDeletable,
                action: () => self.onDelete(),
            },
            {
                key: "entry.cancelAndEmail",
                label: hasGroup
                    ? Language.get("nc_cancel_reservation_group_email")
                    : Language.get("cal_func_res_delete_email"),
                isDisabled:
                    !TimeEdit.isEmailActive ||
                    !isDeletable ||
                    self.props.activeLayer !== 0 ||
                    self.state.canEmail === false,
                action: () => self.onDelete(true),
            },
        ];

        const exportSubmenu = [
            {
                key: "entry.exportAdd",
                label: Language.get("nc_add_to_my_list"),
                action: () =>
                    self.props.openStaticReservationListAdd(self.props.data.reservationids, true),
            },
            {
                key: "entry.exportRemove",
                label: Language.get("nc_remove_from_my_list"),
                action: () =>
                    self.props.openStaticReservationListAdd(self.props.data.reservationids, false),
            },
        ];

        if (self.props.data.groups.length > 0) {
            exportSubmenu.push({
                key: "entry.groupAdd",
                label: Language.get("nc_add_group_to_my_list"),
                action: () => {
                    API.getGroupedEntries(
                        self.props.clusterKind,
                        self.props.data.reservationids,
                        {},
                        (result) => {
                            self.props.openStaticReservationListAdd(
                                _.flatten(result.map((entry) => entry.reservationids)),
                                true
                            );
                        }
                    );
                },
            });
            exportSubmenu.push({
                key: "entry.groupRemove",
                label: Language.get("nc_remove_group_from_my_list"),
                action: () => {
                    API.getGroupedEntries(
                        self.props.clusterKind,
                        self.props.data.reservationids,
                        {},
                        (result) => {
                            self.props.openStaticReservationListAdd(
                                _.flatten(result.map((entry) => entry.reservationids)),
                                false
                            );
                        }
                    );
                },
            });
        }

        const selectOption = self.props.data.hasCapacity()
            ? {
                  key: "entry.select",
                  label: Language.get("nc_option_select"),
                  isDisabled: self.isWrongKind(),
                  action: (event) => {
                      // Override if entry is not clickable, this is the purpose of this select option action.
                      self._isClickable = true;
                      self.onClick(event);
                      self._isClickable = false;
                  },
              }
            : null;

        const infoOption = {
            key: "entry.showInfo",
            label: Language.get("menu_view_info"),
            isDisabled: self.isWrongKind(),
            action: (event) => {
                self.showInfo(event, true);
            },
            shortcut: TimeEdit.presentShortcut("mod+i"),
        };

        const groupOption = {
            key: "entry.group",
            label: Language.get("nc_create_reservation_group"),
            action: () => {
                if (useGroup && self.props.isLegalGroup) {
                    self.props.createGroup();
                } else if (self.props.selectionGroup.length === 0) {
                    self.props.createGroupFromEntry(self.props.data);
                }
            },
            isDisabled:
                readOnly ||
                hasGroup ||
                (self.props.selectionGroup.length > 0 && !self.props.isLegalGroup),
        };

        const ungroupOption = {
            key: "entry.ungroup",
            label: Language.get("nc_ungroup"),
            action: () => {
                self.props.deleteGroup(self.props.data.groups);
            },
        };

        const removeFromGroupOption = {
            key: "entry.removeFromGroup",
            label: Language.get("nc_remove_from_reservation_group"),
            action: () => {
                API.removeReservationsFromReservationGroup(
                    self.props.data.groups[0],
                    self.props.data.reservationids,
                    (result) => {
                        // The call answers a status; an omitted result means success.
                        if ((result.result ?? 0) < 0) {
                            Log.error(Language.get("nc_could_not_remove_from_reservation_group"));
                        } else {
                            self.onUpdate();
                        }
                    }
                );
            },
        };

        const changeTimeOption = hasGroup
            ? {
                  key: "entry.changeTime",
                  label: Language.get("nc_menu_change_time"),
                  isDisabled: self.props.data.lock && self.props.data.lock === "soft",
                  action: () => {
                      this.props.appContext.fireEvent(
                          `entry`,
                          Macros.Event.CHANGE_RESERVATION_TIME,
                          self.props.data.reservationids,
                          true,
                          true
                      );
                  },
              }
            : null;

        const exceptionOption = this.props.appContext.hasReservationExceptions
            ? {
                  key: "entry.editExceptions",
                  label: Language.get("nc_reservation_exception_title"),
                  isDisabled: self.state.canHaveExceptions !== true,
                  action: () => {
                      if (self.props.onEditExceptions) {
                          self.props.onEditExceptions(self.props.data);
                      }
                  },
              }
            : null;

        const editGroupOption = {
            key: "entry.editGroup",
            label: Language.get("nc_edit_reservation_group"),
            isDisabled: !self.props.data.grouped && !readOnly,
            action: () => {
                self.props.editGroup(
                    self.props.data,
                    self.props.data.groups,
                    self.props.data.startTimes[0].getMillenniumDate()
                );
            },
        };

        const changeFieldsOption = {
            key: "entry.editFields",
            label: Language.get("nc_cal_func_res_edit_fields"),
            isDisabled:
                readOnly ||
                (!self.props.enableEditMode && !self.props.data.modifiable) ||
                self.props.selectionGroup.length !== 0,
            action: () => {
                self.handleInfoShortcut(true);
            },
            shortcut: TimeEdit.presentShortcut("mod+e"),
        };

        const changeObjectsOption = {
            key: "entry.changeObject",
            label: Language.get("cal_func_res_change_object"),
            isDisabled:
                readOnly ||
                (!self.props.enableEditMode && !self.props.data.modifiable) ||
                self.props.selectionGroup.length !== 0,
            action: () => {
                self.props.enableEditMode(self.props.data);
            },
            shortcut: TimeEdit.presentShortcut("mod+alt+j"),
        };

        const divider = () => ({
            isSeparator: true,
        });

        const showUngroup = self.props.data.groups
            ? self.props.data.groups.length > 0 &&
              _.some(self.props.data.groups, (group) => group !== 0)
            : false;
        if (self.props.data.grouped && self.props.isGroupMode) {
            // Group mode, entry part of the group
            return [infoOption, divider(), changeObjectsOption, divider(), removeFromGroupOption];
        }
        if (!self.props.data.grouped && self.props.isGroupMode) {
            // Group mode, entry not part of the group
            return [
                infoOption,
                selectOption,
                divider(),
                changeObjectsOption,
                divider(),
                {
                    key: "entry.addToGroup",
                    label: Language.get("nc_add_to_reservation_group"),
                    action: () => {
                        self.props.addToGroup(self.props.data);
                        //self.onUpdate(); Not needed, addToGroup fires modification event
                    },
                    isDisabled: showUngroup && readOnly,
                },
            ];
        }

        const lockOption = this.props.appContext.isLockingEnabled()
            ? {
                  key: "entry.lock",
                  label:
                      self.props.data.lock && self.props.data.lock === "soft"
                          ? Language.get("nc_unlock_reservation")
                          : Language.get("nc_lock_reservation"),
                  action: () => {
                      API.getReservations(self.props.data.reservationids, (reservations) => {
                          if (self.props.data.lock && self.props.data.lock === "soft") {
                              if (
                                  // eslint-disable-next-line no-alert
                                  confirm(
                                      `${Language.get("nc_confirm_unlock")}\n\n${
                                          reservations[0].lockedDescription
                                      }`
                                  )
                              ) {
                                  API.unlockReservations(
                                      self.props.data.reservationids,
                                      (result) => {
                                          self.onUpdate();
                                      }
                                  );
                              }
                          } else {
                              // eslint-disable-next-line no-alert
                              let reason = prompt(
                                  Language.get("nc_reason_for_lock"),
                                  reservations[0].lockedDescription || ""
                              );
                              while (reason !== null && reason.trim() === "") {
                                  // eslint-disable-next-line no-alert
                                  reason = prompt(Language.get("nc_reason_for_lock"));
                              }
                              // User hit cancel
                              if (reason === null) {
                                  return;
                              }
                              API.lockReservations(
                                  self.props.data.reservationids,
                                  reason,
                                  (result) => {
                                      self.onUpdate();
                                  }
                              );
                          }
                      });
                  },
              }
            : null;

        let requestLabel = Language.get("nc_create_exam_request");
        if (this.props.data.reservationids.length > 1) {
            requestLabel = Language.get("nc_create_exam_requests");
        } else if (this.state.hasExamValue) {
            requestLabel = Language.get("nc_edit_exam_request");
        }

        const examRequestOption =
            this.props.appContext.examComponentActive && self.state.hasExamId
                ? {
                      key: "entry.createExamRequest",
                      label: requestLabel,
                      isDisabled: false,
                      action: () =>
                          API.getReservationsByExtid(this.props.data.reservationids, (result) => {
                              let mode = this.state.hasExamValue
                                  ? EXAM_MODE.EDIT
                                  : EXAM_MODE.CREATE;
                              if (result.length > 1) {
                                  mode = EXAM_MODE.CREATE_MULTI;
                              }
                              this.props.appContext.createExamRequest(result, mode);
                          }),
                  }
                : null;

        const capacityOption = self.isCapacityOptionAvailable()
            ? {
                  key: "entry.setCapacity",
                  label: Language.get("nc_set_capacity"),
                  action: () => {
                      const capacity = parseInt(
                          // eslint-disable-next-line no-alert
                          prompt(
                              Language.get("nc_enter_capacity"),
                              `${self.props.data.capacity || 0}`
                          ) || "",
                          10
                      );
                      if (capacity === null || isNaN(capacity)) {
                          return;
                      }
                      const allowUnderCapacity = true;
                      API.setReservationCapacity(
                          self.props.data.reservationids,
                          allowUnderCapacity,
                          capacity,
                          CR_ORDERING.PARALLELL,
                          (statuses) => {
                              // One status per reservation; report the first found failure.
                              const failed = statuses.find((status) => status.details);
                              if (failed?.details) {
                                  Log.error(failed.details);
                              } else {
                                  self.onUpdate();
                              }
                          }
                      );
                  },
              }
            : null;

        const splitReservationOption = self.isSplitOptionAvailable()
            ? {
                  key: "entry.splitReservation",
                  label: Language.get("nc_split_reservation"),
                  isDisabled: self.isSplitOptionDisabled(),
                  action: () => {
                      self.props.splitReservation(self.props.data, (result) => {
                          self.onUpdate();
                      });
                  },
              }
            : null;

        const menu = [
            infoOption,
            selectOption,
            {
                key: "entry.overlappingSubmenu",
                label: Language.get("nc_cal_func_res_show_overlapping_res"),
                isDisabled:
                    self.props.data.overlapCount === 0 || useGroup || self.props.isInOverlapView,
                action: (event) => {
                    self.props.showOverlappingEntries(self.props.data);
                },
                submenu: [
                    {
                        key: "entry.showOverlapping",
                        label: Language.get("nc_show_overlapping_in_calendar"),
                        isDisabled:
                            self.props.data.overlapCount === 0 ||
                            useGroup ||
                            self.props.isInOverlapView,
                        action: (event) => {
                            self.props.showOverlappingEntries(self.props.data);
                        },
                    },
                    {
                        key: "entry.showOverlappingInList",
                        label: Language.get("nc_show_overlapping_in_list"),
                        isDisabled:
                            self.props.data.overlapCount === 0 ||
                            useGroup ||
                            self.props.isInOverlapView,
                        action: (event) => {
                            self.props.showOverlappingEntriesInList(self.props.data);
                        },
                    },
                ],
            },
            divider(),
            changeFieldsOption,
            changeObjectsOption,
            changeTimeOption,
            {
                key: "entry.waitingList",
                label: Language.get("dynamic_reserv_list_reserv_wl_wl_title"),
                isDisabled:
                    readOnly ||
                    !self.props.data.modifiable ||
                    useGroup ||
                    (self.props.data.groups && self.props.data.groups.length > 0),
                submenu: [
                    {
                        key: "entry.toWaitingList",
                        label: Language.get("cal_func_res_move_to_wl"),
                        isDisabled: !self.props.data.modifiable || useGroup || readOnly,
                        action: () => {
                            self.onMoveToWaitingList();
                        },
                    },
                ],
            },
            exceptionOption,
            {
                key: "entry.status",
                label: Language.get("cal_res_side_reservation_status"),
                isDisabled:
                    self.props.readOnlyCalendar ||
                    (self.state.availableStatuses && !self.props.data.modifiable),
                submenu: (self.state.availableStatuses || []).map((status) => ({
                    key: `entry.status.${status.id}`,
                    label: status.getStatusName(),
                    action: () => self.setStatus(status),
                    checked: () => self.state.currentStatus === status,
                })),
            },
            divider(),
            {
                key: "entry.copy",
                label: Language.get("cal_res_below_copy"),
                action: (event) => {
                    self.props.onCopy(self.props.data, event);
                },
                isDisabled: self.props.readOnlyCalendar,
                shortcut: TimeEdit.presentShortcut("mod+c"),
            },
            hasGroup ? editGroupOption : null,
            hasGroup ? ungroupOption : groupOption,
            {
                key: "entry.cancelMenu",
                label: hasGroup
                    ? Language.get("nc_cancel_reservation_group")
                    : Language.get("cal_func_res_delete"),
                isDisabled: !isDeletable,
                submenu: cancelSubmenu,
            },
            divider(),
            {
                key: "entry.sendEmail",
                label: Language.get("cal_func_res_email_reservation"),
                isDisabled: !TimeEdit.isEmailActive,
                action: () => {
                    self.props.emailReservation(self.props.data.reservationids);
                },
            },
            lockOption ? divider() : null,
            lockOption,
            examRequestOption ? divider() : null,
            examRequestOption,
            capacityOption ? divider() : null,
            capacityOption,
            splitReservationOption ? divider() : null,
            splitReservationOption,
        ];

        return menu.filter((option) => option !== null);
    };

    getGroupIds = () => {
        return _.flatten(this.props.selectionGroup.map((entry) => entry.reservationids));
    };

    useGroup = () => {
        const ids = this.getGroupIds();
        return _.some(this.props.data.reservationids, (id) => _.contains(ids, id));
    };

    getGroupOrEntryIds = () => {
        let ids = this.getGroupIds();
        if (this.props.data.groups.length > 0) {
            ids = this.props.getVisibleReservationsInGroups(this.props.data.groups);
        }
        if (_.some(this.props.data.reservationids, (id) => _.contains(ids, id))) {
            return { ids, singleReservation: false };
        }
        return { ids: this.props.data.reservationids, singleReservation: true };
    };

    onMoveToWaitingList = () => {
        if (this.props.data.groups && this.props.data.groups.length > 0) {
            return;
        }
        const data = this.getGroupOrEntryIds();
        this.props.moveToWaitingList(data.ids, data.singleReservation);
    };

    onDelete = (sendEmail = false) => {
        const node = this._isMounted ? ReactDOM.findDOMNode(this) : null;
        const self = this;
        const ids = this.getGroupOrEntryIds().ids;
        this.props.onDelete(ids, sendEmail, (success: boolean) => {
            self.onInfoClose();
            if (node && this._isMounted && success) {
                this._isMounted = false;
                Animation.fallOut(node);
                /*for (let i = 0; i < node.childNodes.length; i++) {
                    Animation.fallOut(node.childNodes.item(i));
                }*/
                self.setState({ waitingForDeletion: true });
            }
        });
    };

    onClick = (event) => {
        event.stopPropagation();

        if (!this._isClickable || this.props.data.kind === EntryKind.NONE) {
            return;
        }

        if (this.state.onClickRecentlyCalled) {
            this.showInfo(event, true);
            return;
        }

        if (!this.props.data.readable) {
            return;
        }

        const addToSelection = _.isModKey(event);
        if (!addToSelection) {
            this.showInfo(event, false);
        }
        this.props.onClick(
            this.props.data,
            addToSelection,
            event.shiftKey,
            this.props.isEntryLocked
        );

        this.setState({ onClickRecentlyCalled: true });
        setTimeout(() => {
            if (this._isMounted) {
                this.setState({ onClickRecentlyCalled: false });
            }
        }, CLICK_TIMEOUT);
    };

    onReservationEditChange = (isEditing) => {
        if (this._isMounted) {
            this.setState({ showEditMode: isEditing });
        }
    };

    onInfoClose = () => {
        if (this._isMounted) {
            this.setState({ showEditMode: false });
        }
    };

    onUpdate = () => {
        this.props.appContext.fireEvent(
            `entry`,
            Macros.Event.RESERVATION_MADE_OR_MODIFIED,
            this.props.data.reservationids,
            false,
            false
        );
    };

    dragStart = (isFirst, isLast, event) => {
        this._isClickable = true;
        if (this.props.data.layer !== this.props.activeLayer) {
            return;
        }

        const leftButtonOrSingleTap = _.isSingleClick(event);
        if (
            !leftButtonOrSingleTap ||
            !this.props.onDragStart ||
            event.ctrlKey ||
            _.isModKey(event) || // This makes mod-clicking to group reservations easy, but requires availabilty overlap drags to add the mod key post-drag-start.
            this.props.data.kind === EntryKind.NONE
        ) {
            return;
        }

        // On background obstacles, let the mouse down event bubble up to Calendar
        if (this.props.data.occupied === false) {
            DragListener.add(event, () => {
                this._isClickable = false;
            });
            return;
        }
        if (!this.props.data.modifiable || this.props.data.isPartial) {
            event.stopPropagation();
            return;
        }

        let dragType = "move";
        let isHighResolution = false;

        if (isFirst || isLast) {
            if (_.matches(event.target, ".dragBottom")) {
                dragType = "resize-end";
            } else if (_.matches(event.target, ".dragTop")) {
                dragType = "resize-start";
            } else if (_.matches(event.target, ".dragRight")) {
                dragType = "resize-end";
            } else if (_.matches(event.target, ".dragLeft")) {
                dragType = "resize-start";
            }
            if (_.matches(event.target, ".dragBottomFine")) {
                dragType = "resize-end";
                isHighResolution = true;
            } else if (_.matches(event.target, ".dragTopFine")) {
                dragType = "resize-start";
                isHighResolution = true;
            } else if (_.matches(event.target, ".dragRightFine")) {
                dragType = "resize-end";
                isHighResolution = true;
            } else if (_.matches(event.target, ".dragLeftFine")) {
                dragType = "resize-start";
                isHighResolution = true;
            }
        }

        this.props.onDragStart(this.props.data, dragType, event, isHighResolution);
    };

    showInfo = (event, showPanel = true, showEditMode = false) => {
        event?.stopPropagation();
        this.props.onInfoOpen(this.props.data.reservationids, showPanel, showEditMode);
    };

    handleInfoShortcut = (showEditMode, event?: React.MouseEvent) => {
        if (showEditMode) {
            this.showInfo(event, true, true);
            return;
        }

        this.showInfo(event, true, false);
    };

    showObjectInfo = (objectId: number) => {
        this.props.onObjectInfo(objectId, false, true);
    };

    onDragOver = (event) => {
        if (
            _.isEventDragDataOfType(event, [
                "application/x-timeedit-object",
                "application/x-timeedit-navbar",
            ])
        ) {
            event.preventDefault();
            this.setState({ isDropTarget: true });
        }
        event.stopPropagation();
    };

    onDragLeave = () => {
        this.setState({ isDropTarget: false });
    };

    onDrop = (event) => {
        let data = _.getDragData(event, "application/x-timeedit-object");
        if (data) {
            event.stopPropagation();
            data = JSON.parse(data);
            this.props.enableEditMode(this.props.data, { addObject: data });
        }
        this.setState({ isDropTarget: false });
    };

    loadTooltip = (forceUpdate) => {
        if (this.props.data.isPadding) {
            if (!forceUpdate) {
                return;
            }
            const mousePosition = this.state.mousePosition;
            if (!mousePosition) {
                return;
            }
            this.props.renderTooltip(
                <PaddingTooltip
                    tooltipLeft={
                        mousePosition.x - mousePosition.offset.left + LEFT_PADDING >
                        this.props.layerSize.width
                    }
                    isVisible={this.props.isTooltipVisible}
                    padding={this.props.data.ownPadding}
                    mousePosition={mousePosition}
                />,
                `${this.props.data.reservationids.join(",")}:padding`
            );
            return;
        }
        if (!this.props.data.readable) {
            // No tooltips for ghost entries or private entries.
            return;
        }

        if (this.state.reservationInfo && !forceUpdate) {
            return;
        }

        Reservation.get(this.props.data.reservationids, (reservations) => {
            if (this._isMounted && this.props.isTooltipVisible) {
                const mousePosition = this.state.mousePosition;
                if (!mousePosition) {
                    return;
                }
                this.setState({ reservationInfo: reservations }, () => {
                    if (this.props.isConflictMode) {
                        this.props.renderTooltip(
                            <ReservationConflictInfoTooltip
                                tooltipLeft={
                                    mousePosition.x -
                                        mousePosition.offset.left +
                                        CONFLICT_TOOLTIP_LEFT_PADDING >
                                    this.props.layerSize.width
                                }
                                isVisible={this.props.isTooltipVisible}
                                onClose={() => this.hideTooltip(true)}
                                mousePosition={mousePosition}
                                conflictingObjects={this.props.data.conflictingObjects}
                                reservationIds={this.props.data.reservationids}
                                showObjectInfo={this.showObjectInfo}
                                moveMemberToScratchpad={this.props.moveMemberToScratchpad}
                                isVisibleInCalendar={this.props.isVisibleInCalendar}
                                presentModal={this.props.appContext.presentModal}
                            />,
                            this.props.data.reservationids.join(",")
                        );
                    } else {
                        this.props.renderTooltip(
                            <ReservationTooltip
                                tooltipLeft={
                                    mousePosition.x - mousePosition.offset.left + LEFT_PADDING >
                                    this.props.layerSize.width
                                }
                                isVisible={this.props.isTooltipVisible}
                                reservations={this.state.reservationInfo}
                                mousePosition={mousePosition}
                                onClose={() => this.hideTooltip(true)}
                                isVisibleInCalendar={this.props.isVisibleInCalendar}
                            />,
                            this.props.data.reservationids.join(",")
                        );
                    }
                });
            }
        });
    };

    trackMouse = (event) => {
        if (!this.props.parentCalendarElemRef) return;
        const offset = _.nodeOffset(this.props.parentCalendarElemRef);
        const clientPos = _.getClientPos(event);
        this.setState({
            mousePosition: { x: clientPos.x, y: clientPos.y, offset },
        });
    };

    hideTooltip = (force = false, mouseMovedToEl?: HTMLElement) => {
        if (mouseMovedToEl?.closest(`.${CONFLICT_INFO_TOOLTIP_CLASSNAME}`)) return;
        if (
            this.props.appContext.user?.tooltipInSidebar &&
            !this.props.isConflictMode &&
            !this.props.data.isPadding
        ) {
            this.props.onDynamicReservationIdsChanged([]);
        } else {
            if (!this.props.isTooltipLocked || force) {
                this.props.requestTooltip(this.props.entryKey, false, force);
                this.setState({ mousePosition: null });
            }
        }
    };

    onMouseOver = () => {
        if (!this.props.isTooltipLocked) {
            this.showTooltip();
        }
    };

    showTooltip = () => {
        if (
            this.props.appContext.user?.tooltipInSidebar &&
            !this.props.isConflictMode &&
            !this.props.data.isPadding
        ) {
            this.props.onDynamicReservationIdsChanged(this.props.data.reservationids);
        } else {
            this.props.requestTooltip(this.props.entryKey, true, false);
        }
    };

    render() {
        if (this.state.waitingForDeletion) {
            return null;
        }

        return (
            <>
                {this.props.styles.map((style, index) =>
                    this.renderSubentry(
                        index === 0,
                        index === this.props.styles.length - 1,
                        _.clone(style),
                        _.clone(this.props.classes[index]),
                        index
                    )
                )}
            </>
        );
    }

    isWrongKind = () => {
        return (
            // eslint-disable-next-line no-magic-numbers
            (this.props.data.kind === 4 && this.props.templateKind.name !== "TEMPLATE_KIND_INFO") ||
            // eslint-disable-next-line no-magic-numbers
            (this.props.data.kind === 5 &&
                this.props.templateKind.name !== "TEMPLATE_KIND_AVAILABILITY")
        );
    };

    renderSubentry = (isFirst, isLast, styles, classes, key) => {
        const WIDTH_MARGIN = 50;
        if (styles.left + styles.width > this.props.layerSize.width - WIDTH_MARGIN) {
            // eslint-disable-next-line no-param-reassign
            classes.leftSideContext = true;
        }
        let text: string | React.ReactNode[] = "";
        let extra: React.JSX.Element | null = null;
        const hasOverlapCount = this.props.data.overlapCount > 1;

        // eslint-disable-next-line no-param-reassign
        styles.width = Math.round(styles.width);
        // eslint-disable-next-line no-param-reassign
        styles.height = Math.round(styles.height);
        const lowHeight = styles.height < MIN_HEIGHT_FOR_TEXT_AND_INFO;

        if (isFirst) {
            if (!classes.ghost) {
                extra = this._renderClusterInfo(lowHeight);
            }

            if (classes.small !== true && !this.props.isTextless) {
                text = this.props.data.text;
            }

            if (this.props.isActiveInfoEntry) {
                // eslint-disable-next-line no-param-reassign
                classes.infoEntry = true;
            }
        }

        if (this.props.data.kind === EntryKind.EXTERNAL) {
            return (
                <div className={_.classSet(classes)} style={styles} key={key}>
                    {text}
                </div>
            );
        }

        const isSizeReservationMode =
            this.props.data.capacity &&
            this.props.isSizeMode &&
            this.props.fluffyHasSizeType &&
            !this.props.data.hasSize(this.props.appContext.user?.useSecondarySize ?? false);

        let infoIcon: React.JSX.Element | null = (
            <span
                className="info"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(evt) => this.showInfo(evt)}
            />
        );
        if (
            // DEV-14766: The info icon sits in the narrow drop strip of a capacity container,
            // stealing clicks meant to place a size reservation into it. Hide it in size mode.
            isSizeReservationMode ||
            classes.ghost ||
            !this.props.data.readable ||
            styles.width < MIN_WIDTH_FOR_INFO_ICON ||
            styles.height < MIN_HEIGHT_FOR_INFO_ICON ||
            this.isWrongKind() ||
            this.props.data.isPadding
        ) {
            infoIcon = null;
        }

        const lockIcon =
            this.props.data.lock && this.props.data.lock === "soft" ? (
                <span className="lock" />
            ) : null;

        const presentZeroIfEmpty = (capacity) => {
            if (capacity === "") {
                return 0;
            }
            return capacity;
        };

        const hasCapacity = this.props.data.hasCapacity();
        const hasSize = this.props.data.hasSize(
            this.props.appContext.user?.useSecondarySize ?? false
        );

        const capacity =
            hasCapacity && !hasSize ? (
                <span className="capacityEntry">
                    {this.props.data.hasRemainingCapacity(
                        this.props.appContext.user?.useSecondarySize ?? false
                    ) ? (
                        <span
                            title={`${Language.get("nc_remaining_capacity")}: ${presentZeroIfEmpty(
                                this.props.data
                                    .getRemainingCapacity(
                                        this.props.appContext.user?.useSecondarySize ?? false
                                    )
                                    .join(", ")
                            )} (${Language.get("nc_total_capacity")}: ${this.props.data
                                .getCapacity()
                                .join(", ")})`}
                        >
                            <span className="capacityLabel">{Language.get("nc_capacity")}</span>:
                            {`${presentZeroIfEmpty(
                                this.props.data
                                    .getRemainingCapacity(
                                        this.props.appContext.user?.useSecondarySize ?? false
                                    )
                                    .join(", ")
                            )} (${this.props.data.getCapacity().join(", ")})`}
                        </span>
                    ) : (
                        <div
                            title={`${Language.get("nc_total_capacity")}: ${this.props.data
                                .getCapacity()
                                .join(", ")}`}
                        >
                            <span className="capacityLabel">{Language.get("nc_capacity")}</span>:
                            {this.props.data.getCapacity().join(", ")}
                        </div>
                    )}
                </span>
            ) : null;

        const size =
            hasSize && !hasCapacity ? (
                <div
                    className="size"
                    title={`${Language.get("nc_size")}: ${this.props.data
                        .getSize(this.props.appContext.user?.useSecondarySize ?? false)
                        .join(", ")}`}
                >
                    <span className="sizeLabel">{Language.get("nc_size")}</span>:
                    {this.props.data
                        .getSize(this.props.appContext.user?.useSecondarySize ?? false)
                        .join(", ")}
                </div>
            ) : null;

        const sizeCapacity =
            hasSize && hasCapacity ? (
                <div
                    className="size"
                    title={`${Language.get("nc_size")}: ${this.props.data
                        .getSize(this.props.appContext.user?.useSecondarySize ?? false)
                        .join(", ")}`}
                >
                    <span className="sizeLabel">{Language.get("nc_size")}</span>:
                    {this.props.data
                        .getSize(this.props.appContext.user?.useSecondarySize ?? false)
                        .join(", ")}
                    /{this.props.data.getCapacity().join(", ")}
                </div>
            ) : null;

        if (classes.ghost) {
            text = [];
            if (isFirst) {
                text.push(
                    <span className="time startTime" key="start">
                        {this.props.data.startTimes[0].format("HH:mm")}
                    </span>
                );
            }
            if (isLast) {
                text.push(
                    <span className="time endTime" key="end">
                        {this.props.data.endTimes[0].format("ee:mm")}
                    </span>
                );
            }

            const shouldShowLength =
                this.props.showLength ||
                (this.props.data.dragType &&
                    this.props.data.dragType !== "move" &&
                    ((isLast && this.props.data.dragType !== "resize-start") ||
                        (isFirst && this.props.data.dragType === "resize-start")));
            if (shouldShowLength) {
                const length = Math.abs(
                    this.props.data.endTimes[0].getMts() - this.props.data.startTimes[0].getMts()
                );
                text.push(
                    <span key="len">
                        <EntryLength
                            containerWidth={styles.width}
                            containerHeight={styles.height}
                            length={length}
                        />
                    </span>
                );
            }
        }
        const hasCapacityInSizeMode =
            capacity &&
            this.props.isSizeMode &&
            this.props.fluffyHasSizeType &&
            !this.props.data.hasSize(this.props.appContext.user?.useSecondarySize ?? false);

        let overlapElement: React.JSX.Element | null = null;
        if (!this.props.data.isSideBySide && !hasCapacityInSizeMode) {
            if (hasOverlapCount && this.props.data.kind !== EntryKind.INFO) {
                overlapElement = (
                    <span className="overlapCount" title={Language.get("nc_overlap_count")}>
                        {this.props.data.overlapCount}
                    </span>
                );
            }
            if (this.props.data.overlapView) {
                overlapElement = (
                    <span className="overlapCount" title={Language.get("nc_overlap_count")}>
                        {this.props.data.reservationids.length}
                    </span>
                );
            }
        }

        let overlapGroupElement: React.JSX.Element | null = null;
        if ((this.props.data.overlapGroup ?? 0) > 0 && classes.standard) {
            overlapGroupElement = (
                <span className="overlapGroupCount" title={Language.get("nc_overlap_group_count")}>
                    {this.props.data.overlapGroup}
                </span>
            );
        }

        const isVerticalTime = this.props.isVertical;
        let dragElements: React.JSX.Element[] | null = [];
        let highResolutionDragElements: React.JSX.Element[] | null = [];
        if (isFirst && isVerticalTime && !classes.yStartPartial && !this.props.data.isPartial) {
            dragElements.push(<div className="dragTop" key={"dt"} />);
            highResolutionDragElements.push(<div className="dragTopFine dragHandle" key={"dtf"} />);
        } else if (
            isFirst &&
            !isVerticalTime &&
            !classes.xStartPartial &&
            !this.props.data.isPartial
        ) {
            dragElements.push(<div className="dragLeft" key={"dl"} />);
            highResolutionDragElements.push(
                <div className="dragLeftFine dragHandle" key={"dlf"} />
            );
        }
        if (isLast && isVerticalTime && !classes.yEndPartial && !this.props.data.isPartial) {
            dragElements.push(<div className="dragBottom" key={"db"} />);
            highResolutionDragElements.push(
                <div className="dragBottomFine dragHandle" key={"dbf"} />
            );
        } else if (
            isLast &&
            !isVerticalTime &&
            !classes.xEndPartial &&
            !this.props.data.isPartial
        ) {
            dragElements.push(<div className="dragRight" key={"dr"} />);
            highResolutionDragElements.push(
                <div className="dragRightFine dragHandle" key={"drf"} />
            );
        }

        if (classes.private || classes.backgroundEntry || classes.isPadding) {
            dragElements = null;
            highResolutionDragElements = null;
        }

        if (
            !_.contains([EntryKind.COMPLETE, EntryKind.GROUP_COMPLETE], this.props.data.kind) ||
            !this.props.isLayerActive
        ) {
            highResolutionDragElements = []; // Only show handles on current entry.
        }

        // eslint-disable-next-line no-param-reassign
        classes.capacity = isSizeReservationMode;
        const remainingCapacities = this.props.data.getRemainingCapacity(
            this.props.appContext.user?.useSecondarySize ?? false
        );
        const hasRemainingCapacity = _.every(
            remainingCapacities,
            (rC) => rC > this.props.fluffySize
        );
        const outOfCapacity = _.any(remainingCapacities, (rC) => rC < 0);
        if (isSizeReservationMode) {
            // eslint-disable-next-line no-param-reassign
            classes.lacksSufficientCapacity = !hasRemainingCapacity;
            // eslint-disable-next-line no-param-reassign
            classes.hasSufficientCapacity = hasRemainingCapacity;
            if (outOfCapacity) {
                // eslint-disable-next-line no-param-reassign
                classes.outOfCapacity = true;
                // eslint-disable-next-line no-param-reassign
                classes.lacksSufficientCapacity = false;
            }
        }
        // eslint-disable-next-line no-param-reassign
        classes.hasGroup = this.props.data.groups && _.some(this.props.data.groups, (gr) => gr > 0);

        const innerStyles: React.CSSProperties = {};
        if (this.props.appContext.user?.fontSize) {
            innerStyles.fontSize = `${this.props.appContext.user?.fontSize}px`;
        }
        if (isSizeReservationMode && this.props.data.capacityOrdering !== CR_ORDERING.SEQUENTIAL) {
            innerStyles.display = "flex";
            innerStyles.flexDirection = this.props.isVertical ? "row" : "column";
            innerStyles.paddingRight = this.props.isVertical ? "14px" : "unset";
            innerStyles.paddingBottom = this.props.isVertical ? "20px" : "30px";
        }

        // eslint-disable-next-line no-param-reassign
        classes.droppable = this.state.isDropTarget;

        let exceptionElement: React.ReactElement | null = null;
        if (this.props.data.memberExceptionCount && this.props.data.memberExceptionCount > 0) {
            const className = this.props.data.complete
                ? "membershipExceptionIndicatorLight"
                : "membershipExceptionIndicatorDark";
            exceptionElement = (
                <div className={className} title={String(this.props.data.memberExceptionCount)} />
            );
        }

        const entryTextClassses = {
            entryText: true,
            capacityEntryText: hasCapacityInSizeMode,
        };
        const entryText = (
            <span className={_.classSet(entryTextClassses)}>
                {exceptionElement}
                {hasCapacityInSizeMode ? "" : text}
                {hasCapacityInSizeMode && capacity}
            </span>
        );

        let innerDiv: React.ReactElement;
        if (lowHeight) {
            innerDiv = (
                <div className={ENTRY_CLASS} style={innerStyles}>
                    <span className="extraInfo lowHeight">
                        {extra}
                        {hasCapacityInSizeMode ? null : entryText}
                        {!hasCapacityInSizeMode && capacity}
                        {size}
                        {sizeCapacity}
                        {overlapElement}
                    </span>
                    {this.props.sizeEntries}
                    {hasCapacityInSizeMode ? entryText : null}
                    {infoIcon}
                    {lockIcon}
                </div>
            );
        } else {
            innerDiv = (
                <div className={ENTRY_CLASS} style={innerStyles}>
                    {hasCapacityInSizeMode ? null : entryText}
                    {!hasCapacityInSizeMode && capacity}
                    {size}
                    {sizeCapacity}
                    <span className="extraInfo">
                        {extra}
                        {overlapElement}
                    </span>
                    {this.props.sizeEntries}
                    {hasCapacityInSizeMode ? entryText : null}
                    {infoIcon}
                    {lockIcon}
                </div>
            );
        }

        if (this.props.data.kind === EntryKind.INFO) {
            return (
                <div className={_.classSet(classes)} style={styles} key={key}>
                    {innerDiv}
                </div>
            );
        }

        let statusIndicator: React.ReactElement | null = null;
        if (classes.incomplete) {
            statusIndicator = <div className="incompleteIndicator topRightCorner" />;
        }
        if (classes.denied) {
            statusIndicator = <div className="rejectedIndicator topRightCorner" />;
        }
        if (classes.requested) {
            statusIndicator = <div className="requestedIndicator topRightCorner" />;
        }
        if (classes.preliminary) {
            statusIndicator = <div className="preliminaryIndicator topRightCorner" />;
        }
        if (classes.planned) {
            statusIndicator = <div className="plannedIndicator topRightCorner" />;
        }
        if ((classes.preliminary || classes.planned) && classes.incomplete) {
            statusIndicator = (
                <div className="incompletePreliminaryPlannedIndicator topRightCorner" />
            );
        }

        if (this.props.borderAndTextOnly) {
            // eslint-disable-next-line no-param-reassign
            styles.background = "none";
            // eslint-disable-next-line no-param-reassign
            styles.pointerEvents = "none";
        }

        const mouseEventHandlerProps: React.HtmlHTMLAttributes<HTMLDivElement> = {
            onTouchStart: this.dragStart.bind(this, isFirst, isLast),
            onMouseDown: this.dragStart.bind(this, isFirst, isLast),
            onDragOver: this.onDragOver,
            onDragLeave: this.onDragLeave,
            onClick: this.onClick,
            onMouseOver: this.onMouseOver,
            onMouseOut: (e) =>
                this.hideTooltip(
                    false,
                    e.relatedTarget instanceof HTMLElement ? e.relatedTarget : undefined
                ),
            onDrop: this.onDrop,
        };

        const identifier = getEntryIdentifier(
            this.props.calendarId,
            this.props.data.reservationids,
            this.props.data.headerObjects,
            this.props.data.isPadding,
            key
        );
        return (
            <div
                {...(!isSizeReservationMode && mouseEventHandlerProps)}
                className={_.classSet(classes)}
                style={styles}
                onMouseMove={
                    this.props.appContext.user?.tooltipInSidebar &&
                    !this.props.isConflictMode &&
                    !this.props.data.isPadding
                        ? _.noop
                        : this.trackMouse
                }
                ref={key}
                key={key}
                id={identifier}
            >
                {statusIndicator}
                {overlapGroupElement}
                {innerDiv}
                {!isSizeReservationMode && dragElements}
            </div>
        );
    };

    _renderClusterInfo = (isLowHeight = false) => {
        if (this.props.data.reservationids.length === 0) {
            return null;
        }

        const clusterValues = this.props.getClusterValues(this.props.data.periods);
        if (clusterValues.length <= 1) {
            return null;
        }

        if (
            this.props.data.reservationids.length < clusterValues.length &&
            this.props.isWeekCluster
        ) {
            const weeks = this.props.data.startTimes.map(
                (dateTime) =>
                    new MillenniumWeek(dateTime, Language.firstDayOfWeek, Language.daysInFirstWeek)
            );
            const displayWeekYear =
                weeks[0].weeksBetween(weeks[weeks.length - 1]) >= TimeConstants.MIN_WEEKS_PER_YEAR;
            const MAX_SECTIONS = 5;
            let wt: string | null = null;
            if (!isLowHeight) {
                // toString maps the formatter over the grouped weeks and drops
                // whatever it answers falsy, so it is handed the index and the
                // whole list as well, and may answer nothing. Its declaration
                // names only the first argument.
                const formatSection = (
                    period: MillenniumWeek | MillenniumWeek[],
                    index: number,
                    allPeriods: (MillenniumWeek | MillenniumWeek[])[]
                ): string | null => {
                    if (index === MAX_SECTIONS && allPeriods.length > MAX_SECTIONS) {
                        return "...";
                    }
                    if (index > MAX_SECTIONS) {
                        return null;
                    }

                    if (Array.isArray(period)) {
                        return Language.formatWeekShortest(
                            period[0].date,
                            period[period.length - 1].date,
                            this.props.appContext.customWeekNames
                        );
                    }
                    return Language.formatSingleWeekShortest(
                        period.date,
                        false,
                        this.props.appContext.customWeekNames
                    );
                };
                const weekText = MillenniumWeek.toString(
                    weeks,
                    formatSection as unknown as (
                        period: MillenniumWeek | MillenniumWeek[]
                    ) => string
                );
                wt = ` (${Language.get("nc_entry_week_short")}${weekText})`;
            }
            return (
                <span key="clusterInfo" className="clusterInfo">
                    <strong>{this.props.data.reservationids.length}</strong>
                    {wt}
                </span>
            );
        }

        return (
            <span key="clusterInfo" className="clusterInfo">
                <strong>{this.props.data.reservationids.length}</strong>
            </span>
        );
    };

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return (
            !_.isEqual(this.props.classes, nextProps.classes) ||
            !_.isEqual(this.props.selectedReservationIds, nextProps.selectedReservationIds) ||
            this.props.clusterDepth !== nextProps.clusterDepth ||
            !_.isEqual(this.props.data, nextProps.data) ||
            this.props.isVertical !== nextProps.isVertical ||
            this.props.isActiveInfoEntry !== nextProps.isActiveInfoEntry ||
            this.props.isLayerShown !== nextProps.isLayerShown ||
            this.props.isTooltipVisible !== nextProps.isTooltipVisible ||
            this.props.isWeekCluster !== nextProps.isWeekCluster ||
            !_.isEqual(this.props.layerSize, nextProps.layerSize) ||
            !_.isEqual(this.props.styles, nextProps.styles) ||
            this.props.isLayerActive !== nextProps.isLayerActive ||
            this.state.waitingForDeletion !== nextState.waitingForDeletion ||
            this.state.reservationInfo !== nextState.reservationInfo ||
            this.state.mousePosition !== nextState.mousePosition ||
            !_.isEqual(this.state.availableStatuses, nextState.availableStatuses) ||
            this.props.appContext.user !== nextContext.user ||
            this.props.sizeEntries !== nextProps.sizeEntries
        );
    }
}

export default withAppContext(Entry);
